<template>
  <!-- HERO SECTION -->
  <Section fullwidth no-padding background="blue">
    <PageHero
      :background="heroBlindMap ? undefined : heroBackground"
      :mobileBackground="heroBlindMap ? undefined : heroMobileBackground"
      :breadcrumbs="breadcrumbs"
      :compact="heroSettings?.hero_compact"
    >
      <template v-if="heroBlindMap" #background>
        <BlindMap :locations="mapLocationsToBlindMap(heroBlindMap.locations)" />
      </template>

      <template #pretitle>
        {{ heroSettings?.hero_pre_title }}
      </template>

      <template #title>
        {{ heroSettings?.hero_title || title || name }}
      </template>

      <template v-if="heroOpeningHours?.hours?.length" #widget>
        <OpeningHours
          :data="heroOpeningHours?.hours"
          :address="heroAddress?.address"
          :map-link="heroAddress?.link"
        />
      </template>

      <template v-if="heroButtons?.length" #buttons>
        <CTA
          v-for="(button, i) in heroButtons"
          :key="i"
          :text="button.hero_button_text"
          :link="getButtonLink(button)"
          :color="!i ? button.hero_button_color || 'red' : 'white'"
          :outline="!!i"
          :query="null"
        />
      </template>

      <template v-else-if="pageableType === 'Career'" #buttons>
        <CTA
          v-if="pageModel?.pdf"
          :text="$t('career.download')"
          :link="pageModel?.pdf"
          icon="download"
          outline
          external
          :query="null"
        />
      </template>

      <template v-if="heroNotice?.notice_text" #footer>
        <Note :text="heroNotice.notice_text" :color="heroNotice.notice_color" />
      </template>
    </PageHero>
  </Section>

  <!-- ANCHORS -->
  <AnchorNavigation v-if="pageAnchors.length" :anchors="pageAnchors" />

  <!-- SPECIAL PAGES CONTENT -->
  <JobDetail
    v-if="pageableType === 'Career'"
    :title="title"
    :content="content"
    :form="form"
    :blocks="dynamicContent"
  />

  <!-- DYNAMIC BLOCKS -->
  <LazyDynamicContentResolver
    v-if="dynamicContent?.length && !['Career'].includes(pageableType)"
    :blocks="dynamicContent as DynamicContentBlocksFragment[]"
    :page-model="pageModel"
  />
</template>

<script lang="ts" setup>
  import type { DynamicContentBlocksFragment } from "~/lib/api-types";
  import { mapLocationsToBlindMap } from "~/lib/data-mappings";
  import { getBreadcrumbs, getResponsiveImage } from "~/lib/helpers";
  import { throwFetchError, throwNotFound } from "~/lib/errors";
  import { openJobModal } from "~/lib/modals"; // do not remove

  const route = useRoute();
  const siteConfig = useSiteConfig();
  const { locale } = useI18n();

  // Parse route reactively
  const path = computed(() =>
    Array.isArray(route.params.slug)
      ? route.params.slug.filter((it) => it !== "").join("/")
      : String(route.params.slug || "")
  );

  // Fetch page data
  const { data, error } = await usePersistedFetch<{
    pageableBySlug?: {
      id?: string;
      full_url?: string;
      model?: any;
      parent?: any;
    } | null;
  }>(
    "GetPageable",
    computed(() => ({ slug: path.value })),
    {
      key: computed(() => `GetPageable:${locale.value}:${path.value}`),
      watch: [() => locale.value, () => route.fullPath, path],
    }
  );

  watch(
    error,
    (err) => {
      if (err) {
        throwFetchError();
      }
    },
    { immediate: true }
  );

  watch(
    data,
    async (value) => {
      if (!value) {
        throwFetchError();
        return;
      }

      if (value.pageableBySlug === null) {
        throwNotFound();
        return;
      }

      const pageable = value.pageableBySlug;

      if (pageable?.id) {
        route.meta.id = pageable.id;
      }

      if (
        pageable?.full_url &&
        ![path.value, `${locale.value}/${path.value}`].includes(
          pageable.full_url
        )
      ) {
        await navigateTo("/" + pageable.full_url, {
          redirectCode: 301,
          replace: true,
        });
      }
    },
    { immediate: true }
  );

  const pageable = computed(() => data.value?.pageableBySlug ?? null);
  const pageModel = computed(() => pageable.value?.model ?? null);

  const metaData = computed(() => pageModel.value?.meta ?? {});
  const title = computed(() => pageModel.value?.title ?? "");
  const name = computed(() => pageModel.value?.name ?? "");
  const dynamicContent = computed(() => pageModel.value?.dynamic_content ?? []);
  const heroSettings = computed(() => pageModel.value?.hero_settings ?? null);
  const hero = computed(() => pageModel.value?.hero ?? null);
  const image = computed(() => pageModel.value?.image ?? null);
  const content = computed(() => pageModel.value?.content ?? null);
  const form = computed(() => pageModel.value?.form ?? null);
  const pageableType = computed(() => pageModel.value?.__typename ?? "");

  const heroButtons = computed(() => {
    const buttons = heroSettings.value?.hero_buttons ?? [];
    return buttons?.[0]?.hero_button_text ? buttons : [];
  });

  const getButtonLink = (button: any) => {
    if (button.hero_button_is_external) {
      return button.hero_button_link;
    }

    if (
      button.hero_button_link?.startsWith("https://be.lobkowicz.cz/storage")
    ) {
      return button.hero_button_link;
    }

    const basePath = button.hero_button_link?.startsWith("/")
      ? button.hero_button_link
      : `/${button.hero_button_link || ""}`;

    if (button.hero_button_query?.slug) {
      const separator = basePath.includes("?") ? "&" : "?";
      return `${basePath}${separator}location=${button.hero_button_query.slug}`;
    }

    return basePath;
  };

  const heroOpeningHours = computed(
    () =>
      heroSettings.value?.extras?.find(
        (it: any) => it.__typename === "ExtraOpeningHours"
      ) ?? null
  );

  const heroAddress = computed(
    () =>
      heroSettings.value?.extras?.find(
        (it: any) => it.__typename === "ExtraAddress"
      ) ?? null
  );

  const heroBlindMap = computed(
    () =>
      heroSettings.value?.extras?.find(
        (it: any) => it.__typename === "ExtraBlindMap"
      ) ?? null
  );

  const heroNotice = computed(
    () =>
      heroSettings.value?.extras?.find(
        (it: any) => it.__typename === "ExtraNotice"
      ) ?? null
  );

  const heroImage = computed(
    () =>
      heroSettings.value?.image?.[0] || hero.value?.[0] || image.value || null
  );

  const heroBackground = computed(
    () => heroSettings.value?.video?.[0] || heroImage.value
  );

  const heroMobileBackground = computed(
    () => heroSettings.value?.video_mobile?.[0] || heroImage.value
  );

  const breadcrumbs = computed(() => getBreadcrumbs(pageable.value));

  const pageAnchors = computed(() =>
    Array.isArray(dynamicContent.value)
      ? dynamicContent.value.reduce<string[]>((anchors, block: any) => {
          if (block?.anchor) anchors.push(block.anchor);
          return anchors;
        }, [])
      : []
  );

  useHead(
    computed(() => ({
      title: metaData.value?.title || title.value,
      meta: [
        { name: "description", content: metaData.value?.description || "" },
        { name: "og:title", content: metaData.value?.og?.title || title.value },
        { name: "og:site_name", content: metaData.value?.og?.site_name || "" },
        { name: "og:type", content: metaData.value?.og?.type || "" },
        { name: "twitter:card", content: metaData.value?.twitter?.card || "" },
        { name: "twitter:site", content: metaData.value?.twitter?.site || "" },
        {
          name: "robots",
          content: metaData.value?.noindex ? "noindex" : "index, follow",
        },
      ],
      link: [
        {
          rel: "canonical",
          href: new URL(route.fullPath, siteConfig.url).href,
        },
        ...(heroImage.value
          ? [
              {
                rel: "preload",
                href: getResponsiveImage(heroImage.value, "3840"),
                as: "image" as const,
              },
            ]
          : []),
      ],
    }))
  );

  definePageMeta({
    middleware: [
      (to) => {
        if (to.path.endsWith(".js")) return abortNavigation();
      },
    ],
  });
</script>
